<script setup lang="ts">
import type { Lesson, LessonSlots } from '../../../model/lessons/interfaces'

import { AppText } from '@shared/ui/typography'
import { ModalDefault } from '@shared/ui/modals'
import { AppDatePicker, NativeSelect } from '@shared/ui/fields'
import { BtnPrimary, BtnSecondary } from '@shared/ui/buttons'

import LessonTutorSelect from '../LessonTutorSelect.vue'
import { useModalLessonReschedule } from '../../../model/lessons/modal-lesson-reschedule.hook'

defineOptions({
  name: 'ModalLessonReschedule',
})

const props = withDefaults(defineProps<{
  lesson: Lesson
  slots?: LessonSlots | null
  /** What the page does once the write succeeded, before the modal closes. */
  afterSave: () => Promise<void>
}>(), {
  slots: null,
})

const emit = defineEmits<{
  close: []
}>()

const {
  refStatusOptions,
  refStatus,
  refCategoryOptions,
  refCategory,
  refReasonOptions,
  refReason,
  refDate,
  refMinDate,
  refStartOptions,
  refStartTime,
  refDurationOptions,
  refDuration,
  refTutorId,
  refTutorOptions,
  refIsTutorsLoading,
  refIsSaving,
  setStatus,
  setCategory,
  setReason,
  setDate,
  setStartTime,
  setDuration,
  close,
  confirm,
} = useModalLessonReschedule({
  lesson: props.lesson,
  slots: props.slots,
  close: () => emit('close'),
  afterSave: props.afterSave,
})
</script>

<template>
  <ModalDefault
    show
    class-name="modal-lesson-dialog"
    title="Reschedule lesson"
    :loading="refIsSaving"
    @click:overlay="close"
  >
    <div class="modal-lesson-reschedule">
      <NativeSelect
        name="select_booking_status"
        label="Status"
        :options="refStatusOptions"
        :value="refStatus"
        :disabled="refIsSaving"
        required
        @change="setStatus"
      />
      <NativeSelect
        name="select_booking_category"
        label="Reason category"
        :options="refCategoryOptions"
        :value="refCategory"
        :disabled="refIsSaving"
        required
        @change="setCategory"
      />
      <NativeSelect
        name="select_booking_reason"
        label="Reasons"
        :options="refReasonOptions"
        :value="refReason"
        :disabled="refIsSaving"
        required
        @change="setReason"
      />
      <AppDatePicker
        class="modal-lesson-reschedule__date"
        label="New date"
        :value="refDate"
        :min-date="refMinDate"
        :disabled="refIsSaving"
        @change="setDate"
      />
      <div class="modal-lesson-reschedule__time">
        <NativeSelect
          class="modal-lesson-reschedule__slot"
          name="select_booking_time"
          label="Time"
          :options="refStartOptions"
          :value="refStartTime"
          :disabled="refIsSaving"
          @change="setStartTime"
        />
        <AppText
          class="modal-lesson-reschedule__separator"
          name="body2-medium"
          color="slate-900"
        >
          to
        </AppText>
        <NativeSelect
          class="modal-lesson-reschedule__slot"
          name="select_booking_duration"
          :options="refDurationOptions"
          :value="refDuration"
          :disabled="refIsSaving"
          @change="setDuration"
        />
      </div>
      <LessonTutorSelect
        v-model="refTutorId"
        label="New tutor"
        :options="refTutorOptions"
        :date="refDate"
        :loading="refIsTutorsLoading"
        :disabled="refIsSaving"
      />
    </div>
    <template #controls>
      <div class="modal-lesson-form__controls">
        <BtnPrimary
          text="Reschedule lesson"
          color="emerald"
          :disabled="refIsSaving"
          @click="confirm"
        />
        <BtnSecondary
          text="Cancel"
          color="black"
          :disabled="refIsSaving"
          @click="close"
        />
      </div>
    </template>
  </ModalDefault>
</template>

<style scoped lang="scss" src="./ModalLessonForm.scss" />
